import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { Button, Checkbox, Form, Icon, Label, Segment, Table } from 'semantic-ui-react';

import { v4 as uuidv4 } from 'uuid';

import { useOrganization } from '../../../hooks/useOrganizations';
import { saveCharge } from '../../../redux/actions/billingActions';

import { addError } from '../../../helpers/errors';
import { isValidPercentageDiscountAmount } from '../../../helpers/invoicePlans';
import {
  DiscountAmountType,
  calculateDiscountAmounts,
  isDiscountCurrency,
  isDiscountPercent,
  isItemOverDiscounted,
} from '../../../helpers/invoices';
import { formatCurrency, formatPercent, parseCurrency } from '../../../helpers/utils';
import InlineError from '../../Messages/InlineError';
import { Dropdown } from '../../Shared/Dropdown';
import { CurrencyInput, PercentInput } from '../../Shared/NumericInput';

import { CATEGORY_OPTIONS, DISCOUNT_OPTIONS, ITEM_OPTIONS_BY_CATEGORY } from './dropdownOptions';

import { getTranslatedOptionsObj } from '../../../helpers/filters';
import './Billing.scss';

const emptyInvoiceItem = {
  id: '',
  category: '',
  item: '',
  notes: '',
  amount: '',
  discounts: [],
};
const emptyDiscount = {
  id: '',
  discountType: '',
  amount: '',
  amountType: DiscountAmountType?.CURRENCY,
};

const getInputType = (amountType) => {
  switch (amountType) {
    case DiscountAmountType.PERCENT:
      return PercentInput;
    default:
      return CurrencyInput;
  }
};

export default function InvoiceItemEditor({ invoiceItem: invoiceItemToEdit, onSave, onCancel }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const organization = useOrganization();
  const invoiceItemRef = useRef('no_id');
  const [invoiceItem, setInvoiceItem] = useState(invoiceItemToEdit ?? emptyInvoiceItem);
  const [errors, setErrors] = useState(null);
  const [saveToLibrary, setSaveToLibrary] = useState(false);
  const [itemOptions, setItemOptions] = useState();

  initializeInvoiceItem(invoiceItem);

  if (!invoiceItem) return null;

  initializeCategoryAndItemOptions(invoiceItem);
  initializeDiscountTypeOptions(invoiceItem.discounts);
  if (!itemOptions && invoiceItem.category) {
    setItemOptions(ITEM_OPTIONS_BY_CATEGORY[invoiceItem.category]);
  }
  return (
    <Segment>
      {renderInvoiceItemEditor()}
      {invoiceItem.discounts?.length > 0 && renderDiscountList()}
      {renderCommands()}
    </Segment>
  );

  function renderInvoiceItemEditor() {
    let categoryOptions = [];
    if (itemOptions) categoryOptions = getTranslatedOptionsObj(itemOptions, t);
    return (
      <Form.Group>
        <Form.Field width={4} error={!!errors?.category}>
          <Label
            basic
            className="label-required"
            style={{ border: 'none' }}
            data-testid="create-recurring-plan-editor-category-label"
          >
            {t('billing.invoiceItems.fields.category')}
          </Label>
          <Dropdown
            data-testid="create-recurring-plan-editor-category-dropdown"
            allowAdditions
            search
            clearable
            required
            label={t('billing.invoiceItems.fields.category')}
            placeholder={t('billing.invoiceItems.placeholders.selectOrCreate')}
            name="category"
            value={invoiceItem.category}
            onAddItem={onAddCategory}
            onChange={onChangeCategory}
            translator={t}
            options={getTranslatedOptionsObj(CATEGORY_OPTIONS, t)}
          />
          {errors?.category && <InlineError text={errors.category} />}
        </Form.Field>
        <Form.Field width={4} error={!!errors?.item}>
          <Label
            basic
            className="label-required"
            style={{ border: 'none' }}
            data-testid="create-recurring-plan-editor-description-label"
          >
            {t('billing.invoiceItems.fields.description')}
          </Label>
          <Dropdown
            data-testid="create-recurring-plan-editor-description-dropdown"
            allowAdditions
            search
            clearable
            required
            placeholder={
              itemOptions?.length
                ? t('billing.invoiceItems.placeholders.selectOrCreate')
                : t('billing.invoiceItems.placeholders.selectCategoryFirst')
            }
            name="item"
            value={invoiceItem.item}
            onAddItem={onAddItem}
            onChange={onChangeInvoiceItem}
            options={categoryOptions}
          />

          {errors?.item && <InlineError text={errors.item} />}
        </Form.Field>
        <Form.Field width={5}>
          <Form.Input
            data-testid="create-recurring-plan-editor-notes"
            type="text"
            value={invoiceItem.notes}
            label={
              <Label basic style={{ border: 'none' }}>
                {t('billing.invoiceItems.fields.notes')}
              </Label>
            }
            onChange={onChangeInvoiceItem}
            placeholder={t('billing.invoiceItemEditor.notesPlaceholder')}
            name="notes"
            maxLength={350}
          />
        </Form.Field>
        <Form.Field width={3} error={!!errors?.amount}>
          <Form.Input
            data-testid="create-recurring-plan-editor-amount"
            required
            type="text"
            value={invoiceItem.amount}
            label={
              <Label basic className="label-required" style={{ border: 'none' }}>
                {t('billing.invoiceItems.fields.amount')}
              </Label>
            }
            onBlur={(e) =>
              onChangeInvoiceItem(e, {
                name: e.target.name,
                value: e.target.value,
              })
            }
            control={CurrencyInput}
            placeholder={t('billing.invoiceItems.fields.amount')}
            name="amount"
          />
          {errors?.amount && <InlineError text={errors.amount} />}
        </Form.Field>
      </Form.Group>
    );
  }

  function renderDiscountEditorRow(discount, discountIndex) {
    const isPercent = isDiscountPercent(discount);
    const isCurrency = isDiscountCurrency(discount);
    const rowError = errors?.discounts?.[discountIndex];

    return (
      <Form.Group className="discount-edit-fields discount-row" key={discount.id}>
        <Form.Field width={7} error={!!rowError?.discountType}>
          <Label basic className="label-required" style={{ border: 'none', backgroundColor: 'transparent' }}>
            {t('billing.invoiceItems.discount.label')}
          </Label>
          <Dropdown
            data-testid={`create-recurring-plan-discount-type-${discountIndex}`}
            allowAdditions
            search
            clearable
            required
            label={t('billing.invoiceItems.discount.label')}
            placeholder={t('billing.invoiceItems.placeholders.selectOrCreate')}
            name="discountType"
            value={discount.discountType}
            onAddItem={onAddDiscountType}
            onChange={(e, data) => onChangeDiscount(e, data, discountIndex)}
            translator={t}
            options={DISCOUNT_OPTIONS}
          />
          {rowError?.discountType && <InlineError text={rowError.discountType} />}
        </Form.Field>
        <Form.Field width={5} error={!!rowError?.amount}>
          <Form.Input
            data-testid={`create-recurring-plan-discount-amount-${discountIndex}`}
            required
            label={
              <Label basic className="label-required" style={{ border: 'none', backgroundColor: 'transparent' }}>
                {isPercent
                  ? t('billing.invoiceItemEditor.discount.percentageLabel')
                  : t('billing.invoiceItemEditor.discount.amountLabel')}
              </Label>
            }
            type="text"
            value={discount.amount}
            onBlur={(e) =>
              onChangeDiscount(
                e,
                {
                  name: e.target.name,
                  value: e.target.value,
                },
                discountIndex
              )
            }
            control={getInputType(discount.amountType)}
            placeholder={
              isPercent
                ? t('billing.invoiceItemEditor.discount.percentagePlaceholder')
                : t('billing.invoiceItemEditor.discount.amountPlaceholder')
            }
            name="amount"
          />
          {rowError?.amount && <InlineError text={rowError.amount} />}
        </Form.Field>
        <Form.Field width={2}>
          <label>&nbsp;</label>
          <Button.Group>
            <Button
              icon
              className={isCurrency ? 'active' : ''}
              onClick={(e) => onClickCurrency(e, discountIndex)}
              data-testid={`create-recurring-plan-discount-amount-type-currency-${discountIndex}`}
            >
              <Icon name="dollar" size="small" />
            </Button>
            <Button
              icon
              className={isPercent ? 'active' : ''}
              onClick={(e) => onClickPercent(e, discountIndex)}
              data-testid={`create-recurring-plan-discount-amount-type-percent-${discountIndex}`}
            >
              <Icon name="percent" size="small" />
            </Button>
          </Button.Group>
        </Form.Field>
        <Form.Field width={2} className="discount-delete-button">
          <label>&nbsp;</label>
          <Button
            data-testid={`create-recurring-plan-discount-delete-${discountIndex}`}
            aria-label={t('billing.invoiceItemEditor.removeDiscount')}
            basic
            icon="trash"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onDeleteDiscount(discountIndex);
            }}
          />
        </Form.Field>
      </Form.Group>
    );
  }
  function renderDiscountList() {
    let totalDiscount = 0;
    return (
      <>
        {invoiceItem.discounts.map((discount, discountIndex) => renderDiscountEditorRow(discount, discountIndex))}
        <Table stackable compact="very" basic="very" className="hide-headers-mobile">
          <Table.Header>
            <Table.Row verticalAlign="middle">
              <Table.HeaderCell width={7} />
              <Table.HeaderCell width={4} data-testid="create-recurring-plan-discounts">
                {t('billing.invoiceItemEditor.discount.typeColumn')}
              </Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="right" data-testid="create-recurring-plan-discount-percent">
                {`${t('billing.invoiceItemEditor.discount.percentColumn')} (%)`}
              </Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="right" data-testid="create-recurring-plan-discount-amount">
                {`${t('billing.invoiceItems.fields.amount')} ($)`}
              </Table.HeaderCell>
              <Table.HeaderCell width={1} />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {invoiceItem.discounts.map((discount, index) => {
              const invalidPercentage =
                isDiscountPercent(discount) && !isValidPercentageDiscountAmount(discount.amount);
              const amounts = invalidPercentage ? null : calculateDiscountAmounts(discount, invoiceItem.amount);
              totalDiscount += Number(amounts?.currencyAmount) || 0;

              return (
                <Table.Row key={`summary-${discount.id}`} verticalAlign="middle">
                  <Table.Cell />
                  <Table.Cell disabled data-testid={`create-recurring-plan-discount-summary-type-${index}`}>
                    {t(discount.discountType)}
                  </Table.Cell>
                  <Table.Cell
                    disabled
                    textAlign="right"
                    data-testid={`create-recurring-plan-discount-summary-percent-${index}`}
                  >
                    {amounts ? formatPercent(amounts.percentAmount) : '—'}
                  </Table.Cell>
                  <Table.Cell
                    disabled
                    textAlign="right"
                    data-testid={`create-recurring-plan-discount-summary-amount-${index}`}
                  >
                    {amounts ? `(-${formatCurrency(amounts.currencyAmount)})` : '—'}
                  </Table.Cell>
                  <Table.Cell />
                </Table.Row>
              );
            })}
            <Table.Row key={'total'} verticalAlign="middle">
              <Table.Cell />
              <Table.Cell />
              <Table.Cell textAlign="right" data-testid="create-recurring-plan-total-label">
                {t('billing.invoiceItemEditor.summary.totalLabel')}
              </Table.Cell>
              <Table.Cell textAlign="right" data-testid="create-recurring-plan-total-amount">
                {formatCurrency((invoiceItem.amount ?? 0) - totalDiscount)}
              </Table.Cell>
              <Table.Cell />
            </Table.Row>
            {errors?.discountTotal && (
              <Table.Row>
                <Table.Cell colSpan={5} textAlign="right" data-testid="create-recurring-plan-discount-total-error">
                  <InlineError text={errors.discountTotal} />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </>
    );
  }
  function renderCommands() {
    return (
      <Form.Group>
        <Form.Field width={10} className="padding-top-24">
          <a
            data-testid="create-recurring-plan-add-discount"
            href="#add-discount"
            onClick={(e) => {
              e.preventDefault();
              onAddDiscount();
            }}
          >
            {t('billing.invoiceItems.actions.addDiscount')}
          </a>
        </Form.Field>
        <Form.Field width={7} className="padding-top-24">
          <Checkbox
            data-testid=" "
            floated="right"
            label={t('billing.invoiceItemEditor.saveToLibraryLabel')}
            checked={saveToLibrary}
            name={'saveToLibrary'}
            onChange={onChangeSaveToLibrary}
          />
        </Form.Field>
        <Form.Field width={11} className="padding-top-24">
          <Button
            primary
            floated="right"
            disabled={false}
            content={t('common.save')}
            onClick={onSaveLocal}
            data-testid="save-button"
          />
          <Button
            floated="right"
            basic
            content={t('common.cancel')}
            onClick={onCancelLocal}
            data-testid="cancel-button"
          />
        </Form.Field>
      </Form.Group>
    );
  }

  function clearInvoiceItem(emptyInvoiceItem) {
    setInvoiceItem(emptyInvoiceItem);
    invoiceItemRef.current = null;
  }

  function initializeInvoiceItem(_invoiceItem) {
    let newInvoiceItem;

    // This is an add operation, create a new empty invoice item
    if (!_invoiceItem?.id) {
      newInvoiceItem = {
        ...emptyInvoiceItem,
        discounts: [],
        id: uuidv4(),
      };

      // It's an edit operation.
    } else if (invoiceItemRef.current !== _invoiceItem.id) {
      newInvoiceItem = {
        ..._invoiceItem,
        discounts: [..._invoiceItem.discounts],
      };
    }
    if (newInvoiceItem) {
      setInvoiceItem(newInvoiceItem);
      invoiceItemRef.current = newInvoiceItem.id;
    }
  }

  // event handlers
  function onSaveLocal(e) {
    e.preventDefault();

    const errors = validateInvoiceItem(invoiceItem, t);

    if (errors) setErrors(errors);
    else onSaveInvoiceItem(invoiceItem);
  }

  function onCancelLocal(e) {
    e.preventDefault();

    if (onCancel) {
      clearInvoiceItem();
      onCancel(false);
    }
  }

  function onAddDiscount() {
    const discounts = invoiceItem.discounts ? [...invoiceItem.discounts] : [];
    discounts.push({ ...emptyDiscount, id: uuidv4() });
    setInvoiceItem({ ...invoiceItem, discounts });
    setErrors(null);
  }
  function onDeleteDiscount(index) {
    const discounts = [...invoiceItem.discounts];
    discounts.splice(index, 1);
    setInvoiceItem({ ...invoiceItem, discounts });
    setErrors(null);
  }
  function onChangeDiscount(e, { name, value }, discountIndex) {
    if (name === 'amount') value = value ? +parseCurrency(value) : 0;

    const discounts = invoiceItem.discounts.map((discount, index) =>
      index === discountIndex ? { ...discount, [name]: value } : discount
    );
    setInvoiceItem({ ...invoiceItem, discounts });
    setErrors(null);
  }
  function onClickCurrency(e, discountIndex) {
    e.preventDefault();
    const discounts = invoiceItem.discounts.map((discount, index) =>
      index === discountIndex ? { ...discount, amountType: DiscountAmountType.CURRENCY } : discount
    );
    setInvoiceItem({ ...invoiceItem, discounts });
  }
  function onClickPercent(e, discountIndex) {
    e.preventDefault();
    const discounts = invoiceItem.discounts.map((discount, index) =>
      index === discountIndex ? { ...discount, amountType: DiscountAmountType.PERCENT } : discount
    );
    setInvoiceItem({ ...invoiceItem, discounts });
  }

  function onChangeInvoiceItem(e, { name, value }) {
    if (name === 'amount') value = value ? +parseCurrency(value) : 0;

    setInvoiceItem({ ...invoiceItem, [name]: value });
    setErrors(null);
  }
  function onSaveInvoiceItem(invoiceItem) {
    if (saveToLibrary) {
      let charge = { ...invoiceItem };
      if (!invoiceItem.id) charge.id = uuidv4();

      dispatch(saveCharge(organization.id, { ...invoiceItem }));
    }
    if (onSave) onSave(invoiceItem);
  }

  function onChangeSaveToLibrary(e, { checked }) {
    setSaveToLibrary(checked);
  }

  function onAddCategory(e, { value }) {
    addCustomCategoryMaybe(value);
    setItemOptions([]);
  }
  function onChangeCategory(e, data) {
    onChangeInvoiceItem(e, data);
    setItemOptions(ITEM_OPTIONS_BY_CATEGORY[data.value]);
  }
  function onAddItem(e, { value }) {
    addCustomItemMaybe(invoiceItem.category, value);
  }
  function onAddDiscountType(e, { value }) {
    addCustomDiscountTypeMaybe(value);
  }
}

function initializeCategoryAndItemOptions(invoiceItem) {
  if (!invoiceItem) return;
  addCustomCategoryMaybe(invoiceItem.category);
  addCustomItemMaybe(invoiceItem.category, invoiceItem.item);
}
function initializeDiscountTypeOptions(discounts) {
  if (!discounts?.length) return;
  discounts.forEach((discount) => addCustomDiscountTypeMaybe(discount.discountType));
}
function addCustomDiscountTypeMaybe(discountType) {
  if (!discountType) return;

  const option = DISCOUNT_OPTIONS.find((o) => o.value === discountType);
  if (!option) {
    DISCOUNT_OPTIONS.push({
      key: discountType,
      value: discountType,
      text: discountType,
    });
  }
}

function addCustomCategoryMaybe(category) {
  if (!category) return;
  const option = CATEGORY_OPTIONS.find((o) => o.value === category);
  if (!option) {
    CATEGORY_OPTIONS.push({ key: category, value: category, text: category });
    ITEM_OPTIONS_BY_CATEGORY[category] = [];
  }
}

function addCustomItemMaybe(category, item) {
  if (!category || !item) return;

  let itemOptions = ITEM_OPTIONS_BY_CATEGORY[category];
  if (!itemOptions) {
    itemOptions = ITEM_OPTIONS_BY_CATEGORY[category] = [];
  }
  const itemOption = itemOptions.find((o) => o.value === item);
  if (!itemOption) {
    itemOptions.push({ key: item, value: item, text: item });
  }
}

function validateInvoiceItem(invoiceItem, _t) {
  let errors = null;

  if (!invoiceItem.category)
    errors = addError(errors, 'category', _t('billing.invoiceItemEditor.validation.categoryRequired'));
  if (!invoiceItem.item)
    errors = addError(errors, 'item', _t('billing.invoiceItemEditor.validation.descriptionRequired'));
  if (!invoiceItem.amount)
    errors = addError(errors, 'amount', _t('billing.invoiceItemEditor.validation.amountRequired'));

  const discountErrors = validateDiscounts(invoiceItem.discounts, _t);
  if (discountErrors) {
    errors = errors ?? {};
    errors.discounts = discountErrors;
  }

  if (invoiceItem.discounts?.length && invoiceItem.amount) {
    if (isItemOverDiscounted(invoiceItem)) {
      errors = addError(errors, 'discountTotal', _t('billing.invoice.itemNegativeError'));
    }
  }

  return errors;
}

function validateDiscounts(discounts, _t) {
  if (!discounts?.length) return null;

  let hasError = false;
  const discountErrors = discounts.map((discount) => {
    const rowError = validateDiscount(discount, _t);
    if (rowError) hasError = true;
    return rowError;
  });

  return hasError ? discountErrors : null;
}

function validateDiscount(discount, _t) {
  let errors = null;
  if (discount) {
    if (!discount.discountType)
      errors = addError(errors, 'discountType', _t('billing.invoiceItemEditor.validation.discountTypeRequired'));
    if (!discount.amount)
      errors = addError(errors, 'amount', _t('billing.invoiceItemEditor.validation.amountRequired'));
  }
  return errors;
}
